import React, { useCallback, useMemo, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { parse, stringify } from 'qs'
import { Box, Tooltip } from '@mui/material'

import { TableUrlPagination } from '../../../../ui'
import { useCombos } from '../../../combos'
import { useIPContext } from '../../contexts'
import { ipTableDataPreparation } from '../../logics'
import { IPCellCUPS } from '../custom-table-cells'
import { IP_TABLE_COLUMNS, DEFAULT_QUERY } from './ip-table.constants'

const getLookup = (combo) => {
  let lookup = {}
  combo.forEach((item) => (lookup[item.key] = item.value))
  return lookup
}

export const IPTable = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const location = useLocation()
  const navigate = useNavigate()
  const combos = useCombos([])
  const {
    data,
    pagination: { total_registros: totalEntries },
    loading,
    search,
    onClickTableRow,
    setSelectedTableRows,
    getTableData,
  } = useIPContext()
  const permissions = useSelector((state) => state.global.permissions)

  useEffect(() => {
    const searchString = location.search.split('?')[1]
    if (searchString) {
      const urlSearchParams = parse(searchString)
      const searchParams = {
        ...DEFAULT_QUERY,
        ...urlSearchParams,
      }
      getTableData(searchParams)
    } else {
      navigate(`/ip?${stringify(DEFAULT_QUERY)}`)
    }
  }, [location.search, getTableData, navigate])

  const handleSelectRows = useCallback(
    (rows) => {
      setSelectedTableRows(rows)
    },
    [setSelectedTableRows]
  )

  const formattedColumns = useMemo(
    () =>
      IP_TABLE_COLUMNS.map((column) => {
        const baseColumn = {
          accessorKey: column.field,
          header: formatMessage({ id: `pages.ip.table.columns.${column.field}.title` }),
          size: column.width,
          muiTableHeadCellProps: {
            style: {
              whiteSpace: 'normal',
              overflow: 'visible',
              textOverflow: 'clip',
            },
          },
        }

        if (column.field === 'cupsCustom') {
          return {
            ...baseColumn,
            Cell: (props) => <IPCellCUPS {...props} />,
          }
        }

        if (column.field === 'via') {
          return {
            ...baseColumn,
            Cell: ({ row }) => {
              const rowData = row.original

              const direccionCompleta =
                `${rowData.tipo_via ?? ''} ${rowData.via ?? ''} ${rowData.numero ?? ''}, ${rowData.poblacion ?? ''} (${rowData.codigopostal ?? ''}), ${rowData.provincia ?? ''}`
                  .replace(/\s+/g, ' ')
                  .trim()

              return (
                <Tooltip title={direccionCompleta} placement="bottom-start">
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {direccionCompleta || '--'}
                  </Box>
                </Tooltip>
              )
            },
          }
        }

        const renderCellWithEllipsis = ({ cell }) => {
          let value = cell.getValue()

          if (
            !column.hasOwnProperty('multiple') &&
            column.comboId &&
            combos[column.comboId]?.data
          ) {
            const lookup = getLookup(combos[column.comboId].data)
            value = lookup[value] ?? value
          }

          const finalValue = value ?? '--'

          return (
            <Tooltip title={finalValue} placement="bottom-start">
              <Box
                component="span"
                sx={{
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {finalValue}
              </Box>
            </Tooltip>
          )
        }

        return {
          ...baseColumn,
          Cell: renderCellWithEllipsis,
        }
      }),
    [combos, formatMessage]
  )

  const processedData = useMemo(
    () => ipTableDataPreparation({ data, combos, intl }),
    [data, combos, intl]
  )

  const tableOptions = useMemo(
    () => ({
      muiTableProps: {
        sx: {
          tableLayout: 'fixed',
        },
      },
      toolbar: false,
      selection: false,
      showSelectAllCheckbox: false,
      draggable: false,
    }),
    []
  )
  const handleRowClick = useCallback(
    (_, row) => {
      onClickTableRow(row.id)
    },
    [onClickTableRow]
  )

  return (
    <TableUrlPagination
      columns={formattedColumns}
      options={tableOptions}
      totalEntries={totalEntries}
      search={search}
      data={processedData}
      isLoading={loading}
      onRowClick={permissions.ver_detalle_ip ? handleRowClick : null}
      onSelectionChange={handleSelectRows}
    />
  )
}
